/**
 * Surface the message an Edge Function actually sent.
 *
 * THE PROBLEM THIS SOLVES. `supabaseClient.functions.invoke` rejects a non-2xx
 * with a `FunctionsHttpError` whose `.message` is the fixed string
 * "Edge Function returned a non-2xx status code" — the server's JSON body is
 * NOT in the message, it is on `.context` (the raw `Response`). So the obvious
 * `(e as Error).message` shows the operator nothing about what went wrong.
 *
 * For our admin functions that is not merely unhelpful, it defeats a guardrail.
 * `feed-purge` refuses an oversized range with
 * `{ error: "Match count exceeded the cap — nothing was deleted", matched_at_least }`
 * — the operator MUST see that, both to know the cap was hit and to know
 * nothing was deleted. Likewise a confirm-phrase mismatch and "Admin role
 * required" are all invisible without this.
 *
 * Returns the server's `error` (plus `hint` when present), falling back to the
 * error's own message and then to `fallback`.
 */
export async function edgeErrorMessage(error: unknown, fallback: string): Promise<string> {
  const ctx = (error as { context?: unknown } | null)?.context as
    | { json?: () => Promise<unknown>; clone?: () => unknown }
    | undefined

  // Duck-typed rather than `instanceof Response` so this also works under jsdom
  // and any fetch polyfill, where the Response constructor may differ.
  if (ctx && typeof ctx.json === 'function') {
    try {
      const source = typeof ctx.clone === 'function' ? (ctx.clone() as { json: () => Promise<unknown> }) : ctx
      const body = (await source.json!()) as { error?: unknown; hint?: unknown } | null
      const parts = [body?.error, body?.hint].filter((v) => typeof v === 'string' && v.trim() !== '')
      if (parts.length) return parts.join(' — ')
    } catch {
      // Body was not JSON, or was already consumed — fall through.
    }
  }

  const msg = (error as Error | null)?.message
  return msg && msg !== 'Edge Function returned a non-2xx status code' ? msg : fallback
}
